/**
 * Per-debtor activity history (localStorage).
 * Tracks contact edits, notes, archive, and related activity on the debtor record.
 */

import { debtorCountryLabel, normalizeDebtorCountry } from './debtorCountry.js';

const KEY = 'demandjet.debtorHistory';

export const DEBTOR_HISTORY_EVENT_TYPES = Object.freeze({
  CREATED: 'CREATED',
  RECORD_UPDATED: 'RECORD_UPDATED',
  NOTE_ADDED: 'NOTE_ADDED',
  NOTE_DELETED: 'NOTE_DELETED',
  DEMAND_STARTED: 'DEMAND_STARTED',
  ARCHIVED: 'ARCHIVED',
  RESTORED: 'RESTORED',
});

const TRACKED_FIELDS = Object.freeze([
  { key: 'Debtor_Company', label: 'company' },
  { key: 'Debtor_Name', label: 'contact name' },
  { key: 'Debtor_Country', label: 'country', kind: 'country' },
  { key: 'Debtor_Street', label: 'street' },
  { key: 'Debtor_City', label: 'city' },
  { key: 'Debtor_State', label: 'state' },
  { key: 'Debtor_Postal_Code', label: 'postal code' },
  { key: 'Debtor_Email', label: 'email' },
  { key: 'External_Account_Number', label: 'account #' },
]);

function readAll() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeAll(all) {
  localStorage.setItem(KEY, JSON.stringify(all || {}));
}

function blank(value) {
  if (value == null) return '';
  return String(value).trim();
}

function normalizeComparable(value, kind, context = {}) {
  if (kind === 'country') {
    return normalizeDebtorCountry(value, {
      postalCode: context.postalCode,
      state: context.state,
    });
  }
  return blank(value);
}

function displayValue(value, kind) {
  const raw = blank(value);
  if (!raw) return '—';
  if (kind === 'country') return debtorCountryLabel(raw) || raw || '—';
  return raw;
}

function joinList(items) {
  if (!items.length) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

/**
 * Build a detailed history summary for debtor contact field changes.
 * @returns {{ type: string, summary: string, changes: Array<{key:string,label:string,from:string,to:string}> }}
 */
export function summarizeDebtorChanges(before = {}, after = {}) {
  const changes = [];
  for (const field of TRACKED_FIELDS) {
    const prevRaw = before?.[field.key];
    const nextRaw = after?.[field.key];
    const prevCmp = normalizeComparable(prevRaw, field.kind, {
      postalCode: before?.Debtor_Postal_Code,
      state: before?.Debtor_State,
    });
    const nextCmp = normalizeComparable(nextRaw, field.kind, {
      postalCode: after?.Debtor_Postal_Code,
      state: after?.Debtor_State,
    });
    if (prevCmp === nextCmp) continue;
    changes.push({
      key: field.key,
      label: field.label,
      from: displayValue(prevRaw, field.kind),
      to: displayValue(nextRaw, field.kind),
    });
  }

  if (!changes.length) {
    return {
      type: DEBTOR_HISTORY_EVENT_TYPES.RECORD_UPDATED,
      summary: 'Contact details saved (no field changes)',
      changes,
    };
  }

  const detailed = changes.slice(0, 4).map(
    (change) => `${change.label} (${change.from} → ${change.to})`
  );
  const extra = changes.length - detailed.length;
  if (extra > 0) detailed.push(`${extra} more field${extra === 1 ? '' : 's'}`);

  return {
    type: DEBTOR_HISTORY_EVENT_TYPES.RECORD_UPDATED,
    summary: `Updated ${joinList(detailed)}`,
    changes,
  };
}

export function createDebtorHistoryEvent({
  debtorId,
  type,
  summary,
  actorName = '',
  actorRole = 'CLIENT',
  createdAt = new Date().toISOString(),
  id = '',
} = {}) {
  return {
    ROWID: id || `dh_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    Debtor_ID: String(debtorId || ''),
    Event_Type: String(type || DEBTOR_HISTORY_EVENT_TYPES.RECORD_UPDATED),
    Summary: String(summary || '').trim() || 'Activity recorded',
    Actor_Name: String(actorName || '').trim() || null,
    Actor_Role: String(actorRole || 'CLIENT').toUpperCase(),
    Created_At: createdAt || new Date().toISOString(),
  };
}

export function sortDebtorHistory(events = []) {
  return [...(events || [])].sort(
    (a, b) => new Date(a.Created_At || 0).getTime() - new Date(b.Created_At || 0).getTime()
  );
}

export function getDebtorHistory(debtorId) {
  const id = String(debtorId || '');
  if (!id) return [];
  const all = readAll();
  const list = Array.isArray(all[id]) ? [...all[id]] : [];
  return sortDebtorHistory(list);
}

export function setDebtorHistory(debtorId, events) {
  const id = String(debtorId || '');
  if (!id) return [];
  const all = readAll();
  all[id] = sortDebtorHistory(events || []);
  writeAll(all);
  return all[id];
}

export function appendDebtorHistory(debtorId, event) {
  const list = getDebtorHistory(debtorId);
  return setDebtorHistory(debtorId, [...list, event]);
}

export function logDebtorHistory(debtorId, partial = {}) {
  const id = String(debtorId || '');
  if (!id) return [];
  const event = createDebtorHistoryEvent({
    debtorId: id,
    ...partial,
  });
  return appendDebtorHistory(id, event);
}

/** Seed a Created event when a debtor has no history yet. */
export function ensureDebtorCreatedHistory(
  debtor,
  { actorName = '', actorRole = 'CLIENT' } = {}
) {
  const id = String(debtor?.id || '');
  if (!id) return [];
  const existing = getDebtorHistory(id);
  if (existing.length) return existing;
  const created = createDebtorHistoryEvent({
    debtorId: id,
    type: DEBTOR_HISTORY_EVENT_TYPES.CREATED,
    summary: 'Debtor contact created',
    actorName,
    actorRole,
    createdAt: debtor.createdAt || debtor.Created_At || new Date().toISOString(),
  });
  return setDebtorHistory(id, [created]);
}

export function remapDebtorHistory(previousId, nextId) {
  const from = String(previousId || '');
  const to = String(nextId || '');
  if (!from || !to || from === to) return getDebtorHistory(to);
  const all = readAll();
  const prior = Array.isArray(all[from]) ? all[from] : [];
  const existing = Array.isArray(all[to]) ? all[to] : [];
  const merged = sortDebtorHistory([
    ...existing,
    ...prior.map((event) => ({ ...event, Debtor_ID: to })),
  ]);
  all[to] = merged;
  delete all[from];
  writeAll(all);
  return merged;
}

export function debtorHistoryTypeLabel(type) {
  switch (String(type || '').toUpperCase()) {
    case DEBTOR_HISTORY_EVENT_TYPES.CREATED:
      return 'Created';
    case DEBTOR_HISTORY_EVENT_TYPES.RECORD_UPDATED:
      return 'Updated';
    case DEBTOR_HISTORY_EVENT_TYPES.NOTE_ADDED:
      return 'Note';
    case DEBTOR_HISTORY_EVENT_TYPES.NOTE_DELETED:
      return 'Note';
    case DEBTOR_HISTORY_EVENT_TYPES.DEMAND_STARTED:
      return 'Demand';
    case DEBTOR_HISTORY_EVENT_TYPES.ARCHIVED:
      return 'Archived';
    case DEBTOR_HISTORY_EVENT_TYPES.RESTORED:
      return 'Restored';
    default:
      return 'Activity';
  }
}

export function noteHistorySnippet(body, max = 72) {
  const text = String(body || '').trim().replace(/\s+/g, ' ');
  if (!text) return 'empty note';
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 1))}…`;
}
